import {Platform} from 'react-native';
import {BasicObject} from '../types';
import {navigate, navGoBack, errorLog, getUrlParams, replace} from '@utils';
import globalStore from '@/services/global.state';
import {getH5MigrationNavigationTarget} from './h5-migration';
import {
  rechargeGiftRouteUnavailable,
  showRechargeGiftModal,
  type RechargeGiftScene,
} from '@/pages/home/components/recharge-gift';
import {matchLinkedPath} from './route-link';

const RECHARGE_GIFT_ROUTE = 'RechargeGift';

const getRechargeGiftLinkScene = (): RechargeGiftScene => {
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.toLowerCase().includes('promotion')
  ) {
    return 'ACTIVITY_CENTER';
  }
  return 'BANNER';
};

export const goBack = () => {
  navGoBack();
};

export const checkToken = () => {
  if (globalStore.token) {
    return true;
  } else {
    goTo('Login');
    return false;
  }
};

const objectToQueryString = (obj: BasicObject) => {
  const keyValuePairs = [];
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      keyValuePairs.push(
        encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]),
      );
    }
  }
  return keyValuePairs.join('&');
};

const parseUriPath = (uri: string) => {
  const nextUri = uri.trim();
  const queryIndex = nextUri.indexOf('?');
  const rawPath = queryIndex > -1 ? nextUri.slice(0, queryIndex) : nextUri;
  const queryString = queryIndex > -1 ? nextUri.slice(queryIndex + 1) : '';

  return {
    path: rawPath.replace(/^\/+/, ''),
    params: queryString ? getUrlParams(queryString) : {},
  };
};

const getPathFromScreenConfig = (screenConfig: unknown) => {
  if (typeof screenConfig === 'string') {
    return screenConfig;
  }
  if (
    screenConfig &&
    typeof screenConfig === 'object' &&
    typeof (screenConfig as BasicObject).path === 'string'
  ) {
    return (screenConfig as BasicObject).path;
  }
};

const getRouteByLinkedPath = (
  path: string,
  params: BasicObject,
): {router: string; params?: BasicObject} | undefined => {
  const linking = globalStore.getItem('linking') as BasicObject | undefined;
  const screens = linking?.config?.screens as BasicObject | undefined;

  if (!screens || !path) {
    return;
  }

  for (const router in screens) {
    if (!screens.hasOwnProperty(router)) {
      continue;
    }

    const screenConfig = screens[router];
    const screenPath = getPathFromScreenConfig(screenConfig);
    const pathParams = matchLinkedPath(screenPath, path);

    if (pathParams) {
      return {
        router,
        params: {...params, ...pathParams},
      };
    }

    const nestedScreens =
      screenConfig &&
      typeof screenConfig === 'object' &&
      (screenConfig as BasicObject).screens;

    if (!nestedScreens || typeof nestedScreens !== 'object') {
      continue;
    }

    for (const childRouter in nestedScreens as BasicObject) {
      if (!(nestedScreens as BasicObject).hasOwnProperty(childRouter)) {
        continue;
      }

      const nestedPath = getPathFromScreenConfig(
        (nestedScreens as BasicObject)[childRouter],
      );
      const nestedPathParams = matchLinkedPath(nestedPath, path);

      if (nestedPathParams) {
        return {
          router,
          params: {
            screen: childRouter,
            params: {...params, ...nestedPathParams},
          },
        };
      }
    }
  }
};

const toRouteNameByPath = (path: string) =>
  path
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

/**
 * @param router 路由名称
 * @param params url参数,如果参数中存在对象,在外部转换
 */
const navigateToRoute = (router: string, params?: BasicObject) => {
  const target = getH5MigrationNavigationTarget(router, params);
  if ('externalUrl' in target) {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.assign(target.externalUrl);
    }
    return;
  }
  router = target.routeName;
  params = target.params;
  // TODO 解决params的类型问题
  if (Platform.OS === 'web') {
    try {
      // TODO 解决兼容web的问题
      navigate(router, params);
    } catch (error) {
      requestAnimationFrame(() => {
        const linking = globalStore.getItem('linking') as BasicObject;
        const screens = linking.config.screens as BasicObject;
        let link = '';
        if (params) {
          link = objectToQueryString(params);
        }
        if (screens[router]) {
          link = screens[router] + (link ? '?' + link : '');
        } else if (screens.Index.screens[router]) {
          link = screens.Index.screens[router] + (link ? '?' + link : '');
        } else {
          link = 'index/home';
          errorLog('route not found:', router);
        }
        window.location.href = window.location.origin + '/' + link;
      });
    }
  } else {
    navigate(router, params);
  }
};

export const goTo = (router: string, params?: BasicObject) => {
  if (router === RECHARGE_GIFT_ROUTE) {
    showRechargeGiftModal(getRechargeGiftLinkScene(), {
      onIneligible: () => rechargeGiftRouteUnavailable.next(),
    });
    return;
  }
  if (router === 'Recharge' && globalStore.token) {
    showRechargeGiftModal('RECHARGE_ENTRY', {
      onUnavailable: () => navigateToRoute(router, params),
    });
    return;
  }
  navigateToRoute(router, params);
};

export function goToByUri(uri?: string) {
  const nextUri = uri?.trim();
  if (!nextUri) {
    return;
  }
  if (nextUri.startsWith('http')) {
    window.location.href = nextUri;
    return;
  }

  if (nextUri.startsWith('route:')) {
    const [route, params] = nextUri.slice(6).split('?');
    goTo(route, getUrlParams(params));
    return;
  }

  if (nextUri.startsWith('/')) {
    const {path, params} = parseUriPath(nextUri);
    const linkedRoute = getRouteByLinkedPath(path, params);

    if (linkedRoute) {
      goTo(linkedRoute.router, linkedRoute.params);
      return;
    }

    if (path.includes('/')) {
      const [parentRoute, childRoute] = path.split('/');
      if (parentRoute === 'index') {
        goTo('Index', {
          screen: childRoute.charAt(0).toUpperCase() + childRoute.slice(1),
          params,
        });
        return;
      }
    }

    const routeName = toRouteNameByPath(path);
    goTo(routeName, params);
    return;
  }
}

export const replaceByUri = (uri?: string) => {
  const nextUri = uri?.trim();
  if (!nextUri) {
    return;
  }
  if (nextUri.startsWith('http')) {
    window.location.replace(nextUri);
    return;
  }

  if (nextUri.startsWith('route:')) {
    const [route, params] = nextUri.slice(6).split('?');
    if (route === RECHARGE_GIFT_ROUTE) {
      goTo(route, getUrlParams(params));
      return;
    }
    replace(route, getUrlParams(params));
    return;
  }

  if (nextUri.startsWith('/')) {
    const {path, params} = parseUriPath(nextUri);
    const linkedRoute = getRouteByLinkedPath(path, params);

    if (linkedRoute) {
      replace(linkedRoute.router, linkedRoute.params);
      return;
    }

    if (path.includes('/')) {
      const [parentRoute, childRoute] = path.split('/');
      if (parentRoute === 'index') {
        replace('Index', {
          screen: childRoute.charAt(0).toUpperCase() + childRoute.slice(1),
          params,
        });
        return;
      }
    }

    const routeName = toRouteNameByPath(path);
    replace(routeName, params);
    return;
  }
};
